import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  FileText, 
  Upload, 
  ArrowRight, 
  ArrowLeft, 
  Globe,
  FileSearch,
  Settings,
  CheckCircle2
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const steps = [
  { 
    number: 1, 
    title: "Import Source", 
    description: "Provide a source for your knowledge base",
    icon: Globe
  },
  { 
    number: 2, 
    title: "Select Articles", 
    description: "Choose the articles to include",
    icon: FileSearch
  },
  { 
    number: 3, 
    title: "Configure Sync", 
    description: "Set up how updates occur",
    icon: Settings
  },
  { 
    number: 4, 
    title: "Review", 
    description: "Confirm your configuration",
    icon: FileText
  },
  { 
    number: 5, 
    title: "Complete", 
    description: "Setup finished",
    icon: CheckCircle2
  }
];

const StepSidebarItem = ({ stepData, currentStep }) => {
  const isActive = stepData.number === currentStep;
  const isCompleted = stepData.number < currentStep;

  return (
    <div 
      className={`flex items-center gap-3 p-3 rounded-lg mb-2 transition-all
        ${isActive ? 'bg-blue-50' : 'hover:bg-gray-50'} 
        ${isCompleted ? 'opacity-60' : ''}`}
    >
      <div 
        className={`w-10 h-10 flex items-center justify-center rounded-full 
          ${isActive ? 'bg-blue-600 text-white' : isCompleted ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600'}`}
      >
        {React.createElement(stepData.icon, { size: 20 })}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className={`text-sm font-medium ${isActive ? 'text-blue-900' : 'text-gray-900'}`}>
          {stepData.title}
        </h3>
        <p className={`text-xs ${isActive ? 'text-blue-800' : 'text-gray-500'}`}>
          {stepData.description}
        </p>
      </div>
    </div>
  );
};

const StepOneContent = ({ url, setUrl }) => (
  <div className="space-y-6">
    <div className="border rounded-lg bg-white">
      <div className="p-4 border-b bg-gray-50">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Import Source</h3>
        <p className="text-sm text-gray-600">Choose how you want to import your knowledge base content</p>
      </div>
      <div className="p-4">
        <Tabs defaultValue="url" className="w-full">
          <TabsList className="mb-6 grid w-full grid-cols-2 bg-gray-100 p-1.5 rounded-lg">
            <TabsTrigger 
              value="url" 
              className="px-4 py-2 text-sm font-medium data-[state=active]:bg-white data-[state=active]:text-indigo-600 rounded-md"
            >
              URL
            </TabsTrigger>
            <TabsTrigger 
              value="file" 
              className="px-4 py-2 text-sm font-medium data-[state=active]:bg-white data-[state=active]:text-indigo-600 rounded-md"
            >
              File Upload
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="url">
            <div className="space-y-4">
              <Label className="text-gray-700 text-sm">Knowledge Base URL</Label>
              <Input 
                type="url" 
                placeholder="Paste your knowledge base URL"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="border-gray-300 rounded-md"
              />
            </div>
          </TabsContent>
          
          <TabsContent value="file">
            <div className="border-2 border-dashed rounded-lg p-6 text-center bg-gray-50">
              <Upload className="h-8 w-8 mx-auto text-gray-400 mb-4" />
              <Button variant="outline" className="bg-white">Choose File</Button>
              <p className="mt-2 text-sm text-gray-500">PDF, CSV, DOCX supported</p>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  </div>
);

const StepTwoContent = ({ selectedArticles, setSelectedArticles }) => {
  const articles = ["Getting Started Guide", "Security Setup", "API Integration", "Best Practices"];
  return (
    <div className="space-y-6">
      <div className="border rounded-lg bg-white">
        <div className="p-4 border-b bg-gray-50">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Select Articles</h3>
          <p className="text-sm text-gray-600">Select articles to import</p>
        </div>
        <div className="p-4 space-y-4">
          <div className="flex justify-between items-center mb-4">
            <Input 
              type="search"
              placeholder="Search articles..."
              className="max-w-sm"
            />
            <Button variant="outline" size="sm">Select All</Button>
          </div>
          <div className="space-y-2">
            {articles.map((article, idx) => (
              <div 
                key={idx} 
                className="flex items-center gap-4 p-3 border rounded-lg hover:bg-gray-50"
              >
                <Checkbox
                  id={`article-${idx}`}
                  checked={selectedArticles.includes(idx)}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setSelectedArticles(prev => [...prev, idx]);
                    } else {
                      setSelectedArticles(prev => prev.filter(id => id !== idx));
                    }
                  }}
                />
                <div>
                  <Label htmlFor={`article-${idx}`} className="font-medium text-gray-900">{article}</Label>
                  <p className="text-sm text-gray-500">Comprehensive documentation with examples</p>
                </div>
                <span className="ml-auto bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">High Quality</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const StepThreeContent = () => (
  <div className="space-y-6">
    <div className="border rounded-lg bg-white">
      <div className="p-4 border-b bg-gray-50">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Configure Sync</h3>
        <p className="text-sm text-gray-600">Set up how your knowledge base stays up to date</p>
      </div>
      <div className="p-4 space-y-2 bg-gray-50 rounded-lg">
        <div className="flex justify-between">
          <span className="text-gray-600 text-sm">Project Id</span>
          <span className="text-gray-900 text-sm">gptfy-ai-playground</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600 text-sm">Bucket Name</span>
          <span className="text-gray-900 text-sm">sgpt-demo-org</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600 text-sm">Status</span>
          <span className="text-gray-900 text-sm">Active</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600 text-sm">Sync Frequency</span>
          <span className="text-gray-900 text-sm">Daily at 22:00</span>
        </div>
      </div>
    </div>
  </div>
);

const StepFourContent = ({ url, selectedArticles }) => (
  <div className="space-y-6">
    <div className="border rounded-lg bg-white">
      <div className="p-4 border-b bg-gray-50">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Review</h3>
        <p className="text-sm text-gray-600">Review your configuration</p>
      </div>
      <div className="p-4 space-y-3">
        <div className="p-3 bg-gray-50 rounded-lg">
          <h4 className="font-medium text-gray-900">Import Source</h4>
          <p className="text-sm text-gray-600">{url || "File upload selected"}</p>
        </div>
        <div className="p-3 bg-gray-50 rounded-lg">
          <h4 className="font-medium text-gray-900">Selected Articles</h4>
          <p className="text-sm text-gray-600">{selectedArticles.length} articles chosen</p>
        </div>
        <div className="p-3 bg-gray-50 rounded-lg">
          <h4 className="font-medium text-gray-900">Sync Settings</h4>
          <p className="text-sm text-gray-600">Project: gptfy-ai-playground</p>
          <p className="text-sm text-gray-600">Bucket: sgpt-demo-org</p>
          <p className="text-sm text-gray-600">Frequency: Daily at 22:00</p>
        </div>
      </div>
    </div>
  </div>
);

const StepFiveContent = ({ selectedArticles }) => (
  <div className="space-y-6 text-center">
    <div className="flex flex-col items-center space-y-4">
      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
        <CheckCircle2 className="h-8 w-8 text-green-600" />
      </div>
      <h2 className="text-lg font-semibold text-gray-900 mb-1">Knowledge Base Ready</h2>
      <p className="text-sm text-gray-600">Your configuration is complete. We’ll start processing your content shortly.</p>
    </div>
    <div className="max-w-sm mx-auto bg-gray-50 rounded-lg p-4 text-left">
      <h4 className="font-medium text-gray-900 mb-2">Setup Summary</h4>
      <div className="space-y-2 text-sm text-gray-700">
        <div className="flex justify-between">
          <span>Articles Imported</span>
          <span>{selectedArticles.length}</span>
        </div>
        <div className="flex justify-between">
          <span>Next Sync</span>
          <span>Today at 22:00</span>
        </div>
      </div>
    </div>
    <Button className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-3 rounded-lg font-medium">
      View Knowledge Base
    </Button>
  </div>
);

const StepContent = ({ step, url, setUrl, selectedArticles, setSelectedArticles }) => {
  switch (step) {
    case 1:
      return <StepOneContent url={url} setUrl={setUrl} />;
    case 2:
      return <StepTwoContent selectedArticles={selectedArticles} setSelectedArticles={setSelectedArticles} />;
    case 3:
      return <StepThreeContent />;
    case 4:
      return <StepFourContent url={url} selectedArticles={selectedArticles} />;
    case 5:
      return <StepFiveContent selectedArticles={selectedArticles} />;
    default:
      return null;
  }
};

const AIKnowledgeWizard = () => {
  const [step, setStep] = useState(1);
  const [url, setUrl] = useState('');
  const [selectedArticles, setSelectedArticles] = useState([]);
  const { logout } = useAuth();

  const handleLogout = async () => {
    try {
      await logout();
      localStorage.removeItem('sf_auth_token');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const nextStep = () => {
    if (step < 5) setStep(step + 1);
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto bg-white rounded-lg shadow-sm border">
        <div className="p-6 space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b pb-4 space-y-4 sm:space-y-0">
            <div>
              <div className="flex items-center space-x-2">
                <div className="bg-blue-600 text-white px-3 py-1 rounded font-bold">GPTfy</div>
                <span className="text-gray-800 text-xl font-semibold">Knowledge Import Wizard</span>
              </div>
              <p className="text-gray-600 mt-1 text-sm">Configure your knowledge base import process</p>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-gray-500 text-sm">Step {step} of 5</span>
              <button 
                onClick={handleLogout}
                className="text-red-500 text-sm hover:text-red-700"
              >
                Logout
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-5 gap-6">
            {/* Steps Sidebar */}
            <div className="col-span-1 sm:col-span-2 sm:border-r sm:pr-6">
              {steps.map((s) => (
                <StepSidebarItem key={s.number} stepData={s} currentStep={step} />
              ))}
            </div>

            {/* Main Content */}
            <div className="col-span-1 sm:col-span-3">
              <StepContent 
                step={step}
                url={url}
                setUrl={setUrl}
                selectedArticles={selectedArticles}
                setSelectedArticles={setSelectedArticles}
              />
            </div>
          </div>

          {/* Navigation */}
          <div className="flex flex-col sm:flex-row justify-between pt-4 border-t space-y-2 sm:space-y-0">
            <Button
              variant="outline"
              onClick={prevStep}
              disabled={step === 1}
              className={`
                px-6 py-2 rounded-lg flex items-center justify-center
                ${step === 1 
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200' 
                  : 'bg-white hover:bg-gray-50 border border-gray-300 text-gray-700'
                }
              `}
            >
              <ArrowLeft className="mr-2 h-5 w-5" />
              Back
            </Button>

            <Button
              onClick={nextStep}
              className="px-6 py-2 rounded-lg bg-[#2563EB] hover:bg-blue-700 text-white flex items-center justify-center font-medium"
            >
              {step === 4 ? 'Confirm' : 'Next'}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIKnowledgeWizard;